import * as React from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { connect } from "react-redux";
import {
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  MainInput,
  centered_flex_box,
  left_flex_box,
  main_button,
} from "../app/components/Styles";
import { isMobile } from "react-device-detect";
import { Image, notification, Space, Table, Tag } from "antd";
import Logo from "../app/components/logo.png";
import PropTypes from "prop-types";
import MuiPhoneNumber from "material-ui-phone-number";
import ImageComponent from "../app/components/ImageComponent";
import { getOrders, addExchange, getProducts } from "../app/store/actions/dataActions";
import { useEffect, useMemo } from "react";
import { AddCircleOutline, Search, Close } from "@mui/icons-material";
import moment from "moment";
import { useState } from "react";
import { Chip, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import CreateTicketModal from "../app/components/CreateTicketModal";

const MAX_REFUND_EGP = 4000;

export const ReturnOrder = ({
  getOrders,
  user,
  order,
  products,
  addExchange,
  getProducts,
  isLoading,
  token
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [helpTicketOpen, setHelpTicketOpen] = useState(false);
  const { t } = useTranslation();
  const [incomingItems, setIncomingItems] = useState([]);
  const [outgoingItems, setOutgoingItems] = useState([]);
  const [outgoingItemsMap, setOutgoingItemsMap] = useState(new Map()); // Map of _id -> product data
  const [refundAmount, setRefundAmount] = useState(0);
  const [reason, setReason] = useState("");
  const isFincart = user?.vendor?.shippingService === "Fincart";
  const [refundType, setRefundType] = useState("refund");

  useEffect(() => {
    if (isFincart) {
      setRefundType("collection");
    }
  }, [isFincart]);
  const [outgoingPage, setOutgoingPage] = useState(1);
  const [outgoingPageSize, setOutgoingPageSize] = useState(10);
  const [outgoingSearch, setOutgoingSearch] = useState("");
  const [outgoingSortField, setOutgoingSortField] = useState('');
  const [outgoingSortOrder, setOutgoingSortOrder] = useState('');

  useEffect(() => {
    getOrders({ orderId: id })
  }, [id])

  useEffect(() => {
    const params = {
      page: outgoingPage,
      limit: outgoingPageSize,
      search: outgoingSearch || undefined,
      ...(outgoingSortField && outgoingSortOrder && { sortOrder: outgoingSortOrder === 'ascend' ? '1' : '-1' })
    };
    getProducts(params);
  }, [outgoingPage, outgoingPageSize, outgoingSortField, outgoingSortOrder]);

  const incomingTotal = useMemo(() => {
    if (!order?.orderItems?.length || incomingItems.length === 0) return 0;
    return incomingItems.reduce((sum, item) => {
      const orderItem = order.orderItems.find((oi) => oi._id === item._id);
      const price = parseFloat(orderItem?.stockItem?.product?.price) || 0;
      const quantity = parseInt(item.quantity, 10) || 0;
      return sum + (price * quantity);
    }, 0);
  }, [incomingItems, order]);

  const outgoingTotal = useMemo(() => {
    if (outgoingItems.length === 0) return 0;
    return outgoingItems.reduce((sum, item) => {
      const product = outgoingItemsMap.get(item._id) || products?.docs?.find((p) => p._id === item._id);
      const price = parseFloat(product?.price) || 0;
      const quantity = parseInt(item.quantity, 10) || 0;
      return sum + (price * quantity);
    }, 0);
  }, [outgoingItems, outgoingItemsMap, products]);

  const netAmount = incomingTotal - outgoingTotal;
  const calculatedRefund = Math.abs(netAmount);

  useEffect(() => {
    setRefundAmount(calculatedRefund);
  }, [calculatedRefund]);

  useEffect(() => {
    if (isFincart) return;
    if (incomingItems.length === 0 && outgoingItems.length === 0) return;
    setRefundType(netAmount >= 0 ? "refund" : "collection");
  }, [netAmount, isFincart, incomingItems.length, outgoingItems.length]);

  const parsedRefundAmount = parseFloat(refundAmount) || 0;
  const exceedsMaxRefund = refundType === "refund" && parsedRefundAmount > MAX_REFUND_EGP;

  const handleSubmit = () => {
    if (exceedsMaxRefund) {
      notification.error({
        message: t("returnOrder.maxRefundExceeded", { max: MAX_REFUND_EGP }),
      });
      return;
    }
    addExchange({ orderId: id, incomingItems, outgoingItems, refundAmount: refundType === "refund" ? -parsedRefundAmount : parsedRefundAmount, reason }, navigate)
  }

  const handleOutgoingSearch = () => {
    setOutgoingPage(1);
    const params = {
      page: 1,
      limit: outgoingPageSize,
      search: outgoingSearch || undefined,
      ...(outgoingSortField && outgoingSortOrder && { sortOrder: outgoingSortOrder === 'ascend' ? '1' : '-1' })
    };
    getProducts(params);
  };

  const handleOutgoingClearSearch = () => {
    setOutgoingSearch('');
    setOutgoingSortField('');
    setOutgoingSortOrder('');
    setOutgoingPage(1);
    const params = {
      page: 1,
      limit: outgoingPageSize
    };
    getProducts(params);
  };

  const handleOutgoingTableChange = (pagination, filters, sorter) => {
    setOutgoingPage(pagination.current);
    setOutgoingPageSize(pagination.pageSize);
    
    // Handle sorting
    if (sorter && sorter.field) {
      setOutgoingSortField(sorter.field);
      setOutgoingSortOrder(sorter.order);
    } else {
      setOutgoingSortField('');
      setOutgoingSortOrder('');
    }
  };

  const returnColumns = [
    {
      title: t("common.sku"),
      dataIndex: 'externalId',
    },
    // {
    //   title: 'Name',
    //   dataIndex: 'name',
    // },
    {
      title: t("common.price"),
      dataIndex: 'price',
      render: (text) => `${text} EGP`,
    },
    {
      title: t("common.size"),
      dataIndex: 'size',
    },
    {
      title: t("orders.ordered"),
      dataIndex: 'quantityOrdered',
      sorter: (a, b) => a.quantityOrdered - b.quantityOrdered,
    },
    {
      title: t("returnOrder.qtyToReturn"),
      render: (_, { _id, quantityReturnable }) => <Space>
        <MainInput
          margin="normal"
          type="number"
          fullWidth
          id={_id}
          disabled={incomingItems.filter((item) => item._id === _id).length === 0}
          inputProps={{
            min: incomingItems.filter((item) => item._id === _id).length > 0 ? 1 : 0,
            max: quantityReturnable
          }}
          value={incomingItems.filter((item) => item._id === _id).length > 0 ? incomingItems.filter((item) => item._id === _id)[0].quantity : 0}
          onChange={({ target }) => onQuantityChange(_id, target.value)}
        />
      </Space>
    },
  ];

  const returnData = order?.orderItems?.map((row) => {
    const quantityReturned = row.quantityReturned || 0;
    return {
      _id: row._id,
      key: row._id,
      externalId: row.stockItem.product.sku || row.stockItem.product.externalId,
      price: row.stockItem.product.price,
      image: row.stockItem.product.image,
      name: row.stockItem.product.name,
      size: row.stockItem.product.size,
      color: row.stockItem.product.color,
      weight: row.stockItem.product.weight,
      quantityOrdered: row.quantityOrdered,
      quantityReturnable: Math.max(0, row.quantityOrdered - quantityReturned),
    }
  })

  const outgoingColumns = [
    {
      title: t("common.sku"),
      dataIndex: 'externalId',
    },
    {
      title: t("common.size"),
      dataIndex: 'size',
    },
    {
      title: t("common.price"),
      dataIndex: 'price',
      sorter: (a, b) => a.price - b.price,
      render: (_, { price }) => `${price} EGP`
    },
    {
      title: t("duplicateOrder.qtyAvailable"),
      dataIndex: 'onHand',
      sorter: (a, b) => a.onHand - b.onHand,
    },
    {
      title: t("duplicateOrder.qtyOnHand"),
      dataIndex: 'amount',
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: t("duplicateOrder.qtyToSend"),
      render: (_, { _id }) => <Space>
        <MainInput
          margin="normal"
          type="number"
          fullWidth
          id={_id}
          disabled={outgoingItems.filter((item) => item._id === _id).length === 0}
          inputProps={{
            min: outgoingItems.filter((item) => item._id === _id).length > 0 ? 1 : 0
          }}
          value={outgoingItems.filter((item) => item._id === _id).length > 0 ? outgoingItems.filter((item) => item._id === _id)[0].quantity : 0}
          onChange={({ target }) => onOutgoingQuantityChange(_id, target.value)}
        />
      </Space>
    },
  ];

  const outgoingData = products?.docs?.map((row) => {
    return {
      _id: row._id,
      key: row._id,
      externalId: row.sku || row.externalId,
      image: row.image,
      name: row.name,
      size: row.size,
      color: row.color,
      weight: row.weight,
      price: row.price,
      unpackedPieces: row.unpackedPieces,
      onHand: row.stocks?.reduce((a, b) => a + b.amount, 0) + row.unpackedPieces,
      amount: row.stocks?.reduce((a, b) => a + b.amount, 0),
    }
  })


  const onOutgoingQuantityChange = (_id, newQuantity) => {
    const newItems = outgoingItems.map((item) => {
      if (item._id === _id) {
        return { ...item, quantity: parseInt(newQuantity) || 1 };
      }
      return item
    });
    setOutgoingItems(newItems);
  };

  const onQuantityChange = (_id, newQuantity) => {
    const newItems = incomingItems.map((item) => {
      if (item._id === _id) {
        return { ...item, quantity: parseInt(newQuantity) || 1 };
      }
      return item
    });
    setIncomingItems(newItems);
  };

  const removeOutgoingItem = (_id) => {
    setOutgoingItems(outgoingItems.filter(item => item._id !== _id));
    const newMap = new Map(outgoingItemsMap);
    newMap.delete(_id);
    setOutgoingItemsMap(newMap);
  };

  // Get current page's row IDs for incoming items (from order)
  const currentPageIncomingIds = returnData?.map(row => row._id) || [];
  // Get current page's row IDs for outgoing items (from products)
  const currentPageOutgoingIds = outgoingData?.map(row => row._id) || [];

  const onSelectChange = (newSelectedRowKeys) => {
    // Get current selections (excluding items from current page)
    const existingSelections = incomingItems.filter(item => !currentPageIncomingIds.includes(item._id));
    
    // Get newly selected items from current page
    const newSelections = newSelectedRowKeys
      .filter(key => currentPageIncomingIds.includes(key))
      .map((key) => {
        const existing = incomingItems.find(item => item._id === key);
        return existing || { _id: key, quantity: 1 };
      });
    
    // Merge: keep existing + new/updated selections
    const merged = [...existingSelections, ...newSelections];
    setIncomingItems(merged);
  };

  const rowSelection = {
    selectedRowKeys: incomingItems
      .filter(item => currentPageIncomingIds.includes(item._id))
      .map(item => item._id),
    onChange: onSelectChange,
  };

  const onOutgoingSelectChange = (newSelectedRowKeys) => {
    // Get current selections (excluding items from current page)
    const existingSelections = outgoingItems.filter(item => !currentPageOutgoingIds.includes(item._id));
    
    // Get newly selected items from current page
    const newSelections = newSelectedRowKeys
      .filter(key => currentPageOutgoingIds.includes(key))
      .map((key) => {
        const existing = outgoingItems.find(item => item._id === key);
        return existing || { _id: key, quantity: 1 };
      });
    
    // Merge: keep existing + new/updated selections
    const merged = [...existingSelections, ...newSelections];
    
    // Update product map for display
    const newMap = new Map(outgoingItemsMap);
    products?.docs?.forEach(product => {
      if (newSelectedRowKeys.includes(product._id)) {
        newMap.set(product._id, product);
      } else if (currentPageOutgoingIds.includes(product._id)) {
        newMap.delete(product._id);
      }
    });
    
    setOutgoingItems(merged);
    setOutgoingItemsMap(newMap);
  };

  const outgoingRowSelection = {
    selectedRowKeys: outgoingItems
      .filter(item => currentPageOutgoingIds.includes(item._id))
      .map(item => item._id),
    onChange: onOutgoingSelectChange,
  };

  const hasSelected = incomingItems.length > 0 && outgoingItems.length > 0

  return (
    <div>
      <Box sx={{ width: "100%", padding: 5, mt: 2 }}>
        <Box sx={{ ...centered_flex_box, mb: 1, flexWrap: "wrap", gap: 2 }}>
          <Typography variant='h4' sx={{ color: "var(--secColor4)" }}>{t("exchangeOrder.title", { orderNumber: order?.shopifyOrderNumber || id })}</Typography>
          <Button sx={{ ...main_button, textTransform: "none" }} onClick={() => setHelpTicketOpen(true)}>
            {t("helpDesk.getHelp")}
          </Button>
        </Box>
        <Box sx={{}}>
          <Box sx={{ ...left_flex_box }}>
            <Grid container spacing={2}>
              <Grid item style={{ ...centered_flex_box }} xs={5}>
                <MainInput
                  margin="normal"
                  label={t("exchangeOrder.exchangeReason")}
                  required
                  value={reason}
                  fullWidth
                  select
                  onChange={({ target }) => setReason(target.value)}
                >
                  <MenuItem value={"Defective Items"}>{t("home.defectiveItems")}</MenuItem>
                  <MenuItem value={"Wrong Color"}>{t("exchanges.wrongColor")}</MenuItem>
                  <MenuItem value={"Size Issue"}>{t("exchanges.sizeIssue")}</MenuItem>
                  <MenuItem value={"Warehouse Mistake"}>{t("home.wrongItems")}</MenuItem>
                </MainInput>
              </Grid>
              <Grid item style={{ ...centered_flex_box }} xs={2}>
                <MainInput
                  margin="normal"
                  label={t("common.type")}
                  value={refundType}
                  fullWidth
                  select
                  onChange={({ target }) => setRefundType(target.value)}
                >
                  {!isFincart && <MenuItem value="refund">{t("modals.cashRefund")}</MenuItem>}
                  <MenuItem value="collection">{t("modals.cashCollection")}</MenuItem>
                </MainInput>
              </Grid>
              <Grid item style={{ ...centered_flex_box }} xs={3}>
                <MainInput
                  margin="normal"
                  type="number"
                  label={refundType === "refund" ? t("exchangeOrder.refundAmount") : t("exchangeOrder.collectionAmount")}
                  value={refundAmount}
                  fullWidth
                  error={exceedsMaxRefund}
                  helperText={exceedsMaxRefund
                    ? t("returnOrder.maxRefundExceeded", { max: MAX_REFUND_EGP })
                    : t("returnOrder.refundAutoCalculated")}
                  inputProps={{ min: 0, max: MAX_REFUND_EGP }}
                  onChange={({ target }) => setRefundAmount(target.value)}
                />
              </Grid>
              <Grid item style={{ ...centered_flex_box }} xs={2}>
                <Button sx={{ ...main_button }} onClick={handleSubmit} disabled={!hasSelected || exceedsMaxRefund || !reason} loading={isLoading}>
                  {t("exchangeOrder.sendExchange")}
                </Button>
                <Typography sx={{ marginInlineStart: 1 }}>{hasSelected ? t("exchangeOrder.exchangingItems", { count: outgoingItems.length }) : null}</Typography>
              </Grid>
            </Grid>
          </Box>
          
          {/* Selected Items Display */}
          {(incomingItems.length > 0 || outgoingItems.length > 0) && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Paper sx={{ p: 2, backgroundColor: 'var(--cardBg)', border: '1px solid var(--borderColor)' }}>
                <Typography variant='h6' sx={{ mb: 2, color: "var(--secColor4)" }}>{t("exchangeOrder.selectedItems")}</Typography>
                <Grid container spacing={2}>
                  {incomingItems.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1, color: "var(--textPrimary)" }}>{t("exchangeOrder.incomingItemsWithCount", { count: incomingItems.length })}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {incomingItems.map((item) => {
                          const orderItem = order?.orderItems?.find(oi => oi._id === item._id);
                          if (!orderItem) return null;
                          const product = orderItem.stockItem.product;
                          return (
                            <Chip
                              key={item._id}
                              label={`${product.sku || product.externalId} (${t("orderDetails.quantity")}: ${item.quantity})${product.size ? ` - ${product.size}` : ''}`}
                              sx={{ 
                                backgroundColor: 'var(--tableRowBg)', 
                                border: '1px solid var(--borderColor)',
                                color: 'var(--textPrimary)'
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                  {outgoingItems.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1, color: "var(--textPrimary)" }}>{t("exchangeOrder.outgoingItemsWithCount", { count: outgoingItems.length })}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {outgoingItems.map((item) => {
                          const product = outgoingItemsMap.get(item._id) || products?.docs?.find(p => p._id === item._id);
                          if (!product) return null;
                          return (
                            <Chip
                              key={item._id}
                              label={`${product.sku || product.externalId} (${t("orderDetails.quantity")}: ${item.quantity})${product.size ? ` - ${product.size}` : ''}`}
                              onDelete={() => removeOutgoingItem(item._id)}
                              deleteIcon={<Close />}
                              sx={{ 
                                backgroundColor: 'var(--tableRowBg)', 
                                border: '1px solid var(--borderColor)',
                                color: 'var(--textPrimary)',
                                '& .MuiChip-deleteIcon': {
                                  color: 'var(--textSecondary)',
                                  '&:hover': {
                                    color: 'var(--primaryColor)'
                                  }
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography textAlign={"center"}>{t("exchangeOrder.incomingItemsTitle")}</Typography>
              <Table
                columns={returnColumns}
                dataSource={returnData}
                showSorterTooltip={{
                  target: 'sorter-icon',
                }}
                rowSelection={rowSelection}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography textAlign={"center"}>{t("exchangeOrder.outgoingItemsTitle")}</Typography>
              <Box sx={{ mb: 2 }}>
                <MainInput
                  margin="normal"
                  placeholder={t("returnsSystem.manageProducts.searchPlaceholder")}
                  value={outgoingSearch}
                  onChange={({ target }) => setOutgoingSearch(target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleOutgoingSearch();
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {outgoingSearch && (
                          <IconButton
                            onClick={handleOutgoingClearSearch}
                            edge="end"
                            sx={{ color: "var(--secColor4)" }}
                          >
                            ×
                          </IconButton>
                        )}
                        <IconButton
                          onClick={handleOutgoingSearch}
                          edge="end"
                          sx={{ color: "var(--secColor4)" }}
                        >
                          <Search />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: '100%', mb: 1 }}
                />
              </Box>
              <Table
                columns={outgoingColumns}
                dataSource={outgoingData}
                loading={isLoading}
                pagination={{
                  current: outgoingPage,
                  pageSize: outgoingPageSize,
                  total: products?.totalDocs,
                  showSizeChanger: true,
                  showQuickJumper: false,
                  showTotal: (total) => `Total ${total} items`,
                }}
                onChange={handleOutgoingTableChange}
                showSorterTooltip={{
                  target: 'sorter-icon',
                }}
                rowSelection={outgoingRowSelection}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <CreateTicketModal
        open={helpTicketOpen}
        onClose={() => setHelpTicketOpen(false)}
        token={token}
        context={{ ticketType: "Order", relevantOrder: id }}
        onSuccess={() => setHelpTicketOpen(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  products: state?.records?.products,
  token: state?.auth?.token,
  order: state?.records?.order
});

const mapDispatchToProps = { getOrders, getProducts, addExchange };

export default connect(mapStateToProps, mapDispatchToProps)(ReturnOrder);
